/** The complete set of editor commands registered against the
 *  command registry by EditorPage. Each command is a pure spec —
 *  the `run` body closes over `get()` which returns the latest
 *  handler object every time, so commands survive across renders
 *  without having to re-register on every state change.
 *
 *  See `command-registry.ts` for the substrate (matchShortcut,
 *  dispatch, fuzzy-search), and `EditorPage.tsx` for the wiring
 *  (handlersRef + register-once useEffect). When adding a new
 *  command:
 *
 *    1. Add the corresponding handler to EditorCommandHandlers.
 *    2. Wire it from EditorPage (typically a useCallback that calls
 *       existing in-component logic).
 *    3. Add a spec here in the right category.
 *    4. Tests in editor-commands.test.ts.
 *
 *  Hidden commands (`hidden: true`) are the contextual modal-mode
 *  shortcuts — Enter applies the crop / transform / pen-path /
 *  path-edit, Escape cancels them. They aren't useful to surface in
 *  the palette ("Apply Crop" is something you press Enter for, not
 *  something you search for) but they need to live in the registry
 *  so the central keydown listener can dispatch them. */

import type { CommandSpec, CommandShortcut } from './command-registry';

export interface EditorCommandHandlers {
  // ─── File ─────────────────────────────────────────────────────
  newDocument: () => void;
  /** Open the Tier 3 Group D template picker dialog. */
  openNewFromTemplate: () => void;
  openLocal: () => void;
  openFias: () => void;
  /** Save to original location (or local download if no handle). */
  save: () => void;
  /** Open the Save-As / Export dialog. */
  openSaveAs: () => void;
  /** Open the Tier 3 Group D save-as-template prompt (captures the
   *  current canvas size + DPI as a user template). */
  openSaveAsTemplate: () => void;
  closeFile: () => void;
  /** Gates File commands that need an open document. Read at
   *  dispatch / availability time, not at registration time. */
  hasActiveFile: boolean;

  // ─── Image ────────────────────────────────────────────────────
  /** Open the Tier 3 Group D Slice 1d Document Settings dialog
   *  (DPI / units / color mode). */
  openDocumentSettings: () => void;
  /** Open the Canvas Size dialog — resizes the canvas without
   *  resampling pixels (anchor-based shift, visual clipping). */
  openCanvasSize: () => void;

  // ─── Edit ─────────────────────────────────────────────────────
  undo: () => void;
  redo: () => void;
  cut: () => void;
  copy: () => void;
  paste: () => void;
  /** Bound to Delete / Backspace. */
  deleteSelected: () => void;

  // ─── Select ───────────────────────────────────────────────────
  selectAll: () => void;
  invertSelection: () => void;

  // ─── View ─────────────────────────────────────────────────────
  zoomIn: () => void;
  zoomOut: () => void;
  fitToScreen: () => void;
  actualSize: () => void;
  toggleRulers: () => void;
  toggleMeasurementGrid: () => void;
  toggleGuidesVisible: () => void;
  toggleTransparencyChecker: () => void;
  toggleSnapToGrid: () => void;
  toggleSnapToGuides: () => void;
  toggleSmartGuides: () => void;
  toggleLockGuides: () => void;
  clearGuides: () => void;
  openNewGuide: () => void;
  openGridSettings: () => void;

  // ─── Tools (palette selection) ────────────────────────────────
  selectTool: (tool: string) => void;
  /** Retouch sub-tool cycling (Shift+R while Retouch is active). */
  cycleRetouchVariant: () => void;

  // ─── Transform / modal-mode ───────────────────────────────────
  beginTransform: () => void;
  applyTransform: () => void;
  cancelTransform: () => void;
  beginLiquify: () => void;
  applyCrop: () => void;
  finalizePenPath: () => void;
  cancelPenPath: () => void;
  applyPathEdit: () => void;
  cancelPathEdit: () => void;

  // ─── History ──────────────────────────────────────────────────
  /** Photoshop's "New Snapshot" — capture a named, pinned anchor
   *  point in the history. Cap-exempt so a user can return to it
   *  even after 200 subsequent steps push out the regular timeline. */
  historyManualSnapshot: () => void;
  /** Show or hide the History panel (right-edge floating dock). */
  toggleHistoryPanel: () => void;

  // ─── Help / meta ──────────────────────────────────────────────
  openHelp: () => void;
  openPalette: () => void;
  openKeyboardSettings: () => void;
}

/** The tool ids that get a single-letter shortcut, in the legacy
 *  order. The id namespace (`tool.<name>`) is used both as the
 *  registry command id and (with the `tool.` prefix stripped) as
 *  the argument to `selectTool`. Exported so the test suite can
 *  assert every tool has a shortcut. */
export const TOOL_SHORTCUTS: ReadonlyArray<{ tool: string; label: string; key: string }> = [
  { tool: 'move', label: 'Move tool', key: 'v' },
  { tool: 'marquee', label: 'Marquee tool', key: 'm' },
  { tool: 'crop', label: 'Crop tool', key: 'c' },
  { tool: 'draw', label: 'Draw tool', key: 'b' },
  { tool: 'text', label: 'Text tool', key: 't' },
  { tool: 'shape', label: 'Shape tool', key: 'u' },
  { tool: 'eraser', label: 'Eraser tool', key: 'e' },
  { tool: 'fill', label: 'Fill tool', key: 'g' },
  { tool: 'retouch', label: 'Retouch tool', key: 'r' },
  { tool: 'slice', label: 'Slice tool', key: 's' },
  { tool: 'ai', label: 'AI tool', key: 'a' },
];

/** History-entry labels for tool-bound actions. Many editor
 *  actions (brush strokes, marquee selects, erase passes) hit
 *  `history.saveSnapshot()` from canvas event handlers without
 *  going through the command registry — those have no command id
 *  to label by, so the history hook falls back to the active
 *  tool's entry here. The values intentionally read as past-tense
 *  actions, not the tool's display name, so the panel reads
 *  "Brush" / "Erase" rather than "Draw tool" / "Eraser tool". */
export const TOOL_HISTORY_LABELS: Readonly<Record<string, string>> = {
  move: 'Move',
  marquee: 'Marquee select',
  crop: 'Crop',
  draw: 'Brush',
  text: 'Text edit',
  shape: 'Shape',
  eraser: 'Erase',
  fill: 'Fill',
  retouch: 'Retouch',
  slice: 'Slice',
  // Pen tool isn't in TOOL_SHORTCUTS (no single-letter binding —
  // selected from the tool palette / pen-edit affordances) but it
  // is a valid `activeTool` value and produces history entries
  // when the user clicks anchors to build a path.
  pen: 'Pen',
  ai: 'AI edit',
};

/** Build the complete spec list. `get` returns the latest
 *  handler-set on every invocation so commands always see fresh
 *  state — see the EditorPage handlersRef pattern. */
export function buildEditorCommands(get: () => EditorCommandHandlers): CommandSpec[] {
  const specs: CommandSpec[] = [];

  // ─── File ─────────────────────────────────────────────────────
  specs.push(
    {
      id: 'file.new',
      label: 'New',
      category: 'file',
      description: 'Create a new blank document',
      run: () => get().newDocument(),
    },
    {
      id: 'file.new-from-template',
      label: 'New from Template...',
      category: 'file',
      description: 'Create a new document from a built-in or saved template',
      run: () => get().openNewFromTemplate(),
    },
    {
      id: 'file.open-local',
      label: 'Open Local...',
      category: 'file',
      description: 'Open an image from your device',
      run: () => get().openLocal(),
    },
    {
      id: 'file.open-fias',
      label: 'Open from Fias...',
      category: 'file',
      description: 'Open an image saved to Fias storage',
      run: () => get().openFias(),
    },
    {
      id: 'file.save',
      label: 'Save',
      category: 'file',
      description: 'Save to the current location',
      isAvailable: () => get().hasActiveFile,
      run: () => get().save(),
    },
    {
      id: 'file.save-as',
      label: 'Save As...',
      category: 'file',
      description: 'Open the Export dialog',
      shortcuts: [{ key: 's', mod: true }],
      isAvailable: () => get().hasActiveFile,
      run: () => get().openSaveAs(),
    },
    {
      id: 'file.save-as-template',
      label: 'Save as Template...',
      category: 'file',
      description: "Save the active document's canvas size as a reusable template",
      isAvailable: () => get().hasActiveFile,
      run: () => get().openSaveAsTemplate(),
    },
    {
      id: 'file.close',
      label: 'Close',
      category: 'file',
      description: 'Close the active document',
      isAvailable: () => get().hasActiveFile,
      run: () => get().closeFile(),
    },
  );

  // ─── Image ────────────────────────────────────────────────────
  specs.push({
    id: 'image.document-settings',
    label: 'Document Settings...',
    category: 'image',
    description: 'Edit DPI, document size in physical units, and color mode metadata',
    isAvailable: () => get().hasActiveFile,
    run: () => get().openDocumentSettings(),
  });
  specs.push({
    id: 'image.canvas-size',
    label: 'Canvas Size...',
    category: 'image',
    description:
      'Resize the canvas without resampling pixels; pick a 3×3 anchor for where the existing image stays',
    isAvailable: () => get().hasActiveFile,
    run: () => get().openCanvasSize(),
  });

  // ─── Edit ─────────────────────────────────────────────────────
  // Redo before Undo so Ctrl+Shift+Z resolves to Redo before falling
  // through to Undo (Ctrl+Z) by Shift-modifier rejection.
  specs.push(
    {
      id: 'edit.undo',
      label: 'Undo',
      category: 'edit',
      shortcuts: [{ key: 'z', mod: true }],
      run: () => get().undo(),
    },
    {
      id: 'edit.redo',
      label: 'Redo',
      category: 'edit',
      shortcuts: [
        // Ctrl+Y (Win/Linux convention) AND Ctrl+Shift+Z (Mac
        // convention) — both bind to the same command.
        { key: 'y', mod: true },
        { key: 'z', mod: true, shift: true },
      ],
      run: () => get().redo(),
    },
    {
      id: 'edit.cut',
      label: 'Cut',
      category: 'edit',
      shortcuts: [{ key: 'x', mod: true }],
      run: () => get().cut(),
    },
    {
      id: 'edit.copy',
      label: 'Copy',
      category: 'edit',
      shortcuts: [{ key: 'c', mod: true }],
      run: () => get().copy(),
    },
    {
      id: 'edit.paste',
      label: 'Paste',
      category: 'edit',
      shortcuts: [{ key: 'v', mod: true }],
      run: () => get().paste(),
    },
    {
      id: 'edit.delete',
      label: 'Delete',
      category: 'edit',
      // Two shortcuts on one command — both Delete and Backspace
      // delete the selected layer / pixels.
      shortcuts: [{ key: 'Delete' }, { key: 'Backspace' }],
      run: () => get().deleteSelected(),
    },
  );

  // ─── Select ───────────────────────────────────────────────────
  specs.push(
    {
      id: 'select.all',
      label: 'Select All',
      category: 'select',
      shortcuts: [{ key: 'a', mod: true }],
      run: () => get().selectAll(),
    },
    {
      id: 'select.invert',
      label: 'Invert Selection',
      category: 'select',
      shortcuts: [{ key: 'i', mod: true, shift: true }],
      run: () => get().invertSelection(),
    },
  );

  // ─── View ─────────────────────────────────────────────────────
  // Code-based shortcuts (Digit0, Digit1, Equal, Minus, KeyR,
  // Quote, Semicolon) so AZERTY / QWERTZ users hit the same
  // physical positions.
  specs.push(
    {
      id: 'view.zoom-in',
      label: 'Zoom In',
      category: 'view',
      shortcuts: [
        { key: '=', code: 'Equal', mod: true },
        { key: '+', code: 'Equal', mod: true, shift: true },
      ],
      run: () => get().zoomIn(),
    },
    {
      id: 'view.zoom-out',
      label: 'Zoom Out',
      category: 'view',
      shortcuts: [{ key: '-', code: 'Minus', mod: true }],
      run: () => get().zoomOut(),
    },
    {
      id: 'view.fit-screen',
      label: 'Fit on Screen',
      category: 'view',
      shortcuts: [{ key: '0', code: 'Digit0', mod: true }],
      run: () => get().fitToScreen(),
    },
    {
      id: 'view.actual-size',
      label: 'Actual Pixels (100%)',
      category: 'view',
      shortcuts: [{ key: '1', code: 'Digit1', mod: true }],
      run: () => get().actualSize(),
    },
    {
      id: 'view.toggle-rulers',
      label: 'Toggle Rulers',
      category: 'view',
      shortcuts: [{ key: 'r', code: 'KeyR', mod: true }],
      run: () => get().toggleRulers(),
    },
    {
      id: 'view.toggle-grid',
      label: 'Toggle Measurement Grid',
      category: 'view',
      shortcuts: [{ key: "'", code: 'Quote', mod: true }],
      run: () => get().toggleMeasurementGrid(),
    },
    {
      id: 'view.toggle-guides',
      label: 'Toggle Guides',
      category: 'view',
      shortcuts: [{ key: ';', code: 'Semicolon', mod: true }],
      run: () => get().toggleGuidesVisible(),
    },
    // No-shortcut commands below — palette- / menu-discoverable.
    // Photoshop binds shortcuts only to the rulers/grid/guides
    // visibility toggles above; the rest stay menu-only to avoid
    // colliding with the bare letter shortcuts used by tools.
    {
      id: 'view.toggle-transparency-checker',
      label: 'Toggle Transparency Checker',
      category: 'view',
      description: 'Show or hide the checkerboard pattern behind transparent pixels',
      run: () => get().toggleTransparencyChecker(),
    },
    {
      id: 'view.toggle-snap-grid',
      label: 'Toggle Snap to Grid',
      category: 'view',
      run: () => get().toggleSnapToGrid(),
    },
    {
      id: 'view.toggle-snap-guides',
      label: 'Toggle Snap to Guides',
      category: 'view',
      run: () => get().toggleSnapToGuides(),
    },
    {
      id: 'view.toggle-smart-guides',
      label: 'Toggle Smart Guides',
      category: 'view',
      description: 'Auto-align hints when dragging layers; hold Ctrl to suppress',
      run: () => get().toggleSmartGuides(),
    },
    {
      id: 'view.toggle-lock-guides',
      label: 'Toggle Lock Guides',
      category: 'view',
      run: () => get().toggleLockGuides(),
    },
    {
      id: 'view.clear-guides',
      label: 'Clear All Guides',
      category: 'view',
      run: () => get().clearGuides(),
    },
    {
      id: 'view.new-guide',
      label: 'New Guide...',
      category: 'view',
      description: 'Insert a horizontal or vertical guide at a numeric position',
      run: () => get().openNewGuide(),
    },
    {
      id: 'view.open-grid-settings',
      label: 'Grid Settings...',
      category: 'view',
      description: 'Configure measurement grid spacing and color',
      run: () => get().openGridSettings(),
    },
  );

  // ─── Tools ────────────────────────────────────────────────────
  for (const t of TOOL_SHORTCUTS) {
    specs.push({
      id: `tool.${t.tool}`,
      label: t.label,
      category: 'tools',
      shortcuts: [{ key: t.key }],
      run: () => get().selectTool(t.tool),
    });
  }
  // Retouch sub-tool cycling — Shift+R, only while Retouch is the
  // active tool. Registered AFTER `tool.retouch` so the bare-`r`
  // shortcut still wins for the no-modifier case.
  specs.push({
    id: 'tool.retouch.cycle-variant',
    label: 'Cycle Retouch Variant',
    category: 'tools',
    description: 'Switch between Heal / Patch / Stamp variants',
    shortcuts: [{ key: 'r', shift: true }],
    isAvailable: (ctx) => ctx.activeTool === 'retouch',
    run: () => get().cycleRetouchVariant(),
  });

  // ─── Transform / modal-mode ───────────────────────────────────
  // Begin-mode entry points are surfaced in the palette; modal-mode
  // Enter / Escape commands are hidden because they're context-only
  // and would clutter the palette list.
  specs.push(
    {
      id: 'transform.begin',
      label: 'Free Transform',
      category: 'transform',
      description: 'Move / scale / rotate / skew the active layer',
      shortcuts: [{ key: 't', mod: true }],
      isAvailable: (ctx) => ctx.hasActiveLayer && !ctx.transformActive,
      run: () => get().beginTransform(),
    },
    {
      id: 'transform.liquify',
      label: 'Liquify',
      category: 'transform',
      description: 'Push / pull / pinch / bulge the active layer',
      shortcuts: [{ key: 'x', mod: true, shift: true }],
      isAvailable: (ctx) => ctx.hasActiveLayer,
      run: () => get().beginLiquify(),
    },
    // Crop apply (Enter while Crop tool is active). The Crop case
    // is registered BEFORE transform.apply so a user holding Crop
    // and somehow also having transformActive (shouldn't happen)
    // still gets Crop semantics — matches the legacy if-else order.
    {
      id: 'crop.apply',
      label: 'Apply Crop',
      category: 'transform',
      hidden: true,
      shortcuts: [{ key: 'Enter' }],
      isAvailable: (ctx) => ctx.activeTool === 'crop',
      run: () => get().applyCrop(),
    },
    {
      id: 'transform.apply',
      label: 'Apply Transform',
      category: 'transform',
      hidden: true,
      shortcuts: [{ key: 'Enter' }],
      isAvailable: (ctx) => ctx.transformActive,
      run: () => get().applyTransform(),
    },
    {
      id: 'transform.cancel',
      label: 'Cancel Transform',
      category: 'transform',
      hidden: true,
      shortcuts: [{ key: 'Escape' }],
      isAvailable: (ctx) => ctx.transformActive,
      run: () => get().cancelTransform(),
    },
    {
      id: 'pen.finalize',
      label: 'Finalize Pen Path',
      category: 'transform',
      hidden: true,
      shortcuts: [{ key: 'Enter' }],
      isAvailable: (ctx) => ctx.activeTool === 'pen' && !ctx.transformActive,
      run: () => get().finalizePenPath(),
    },
    {
      id: 'pen.cancel',
      label: 'Cancel Pen Path',
      category: 'transform',
      hidden: true,
      shortcuts: [{ key: 'Escape' }],
      isAvailable: (ctx) => ctx.activeTool === 'pen' && !ctx.transformActive,
      run: () => get().cancelPenPath(),
    },
    {
      id: 'path-edit.apply',
      label: 'Apply Path Edit',
      category: 'transform',
      hidden: true,
      shortcuts: [{ key: 'Enter' }],
      isAvailable: (ctx) => ctx.pathEditActive,
      run: () => get().applyPathEdit(),
    },
    {
      id: 'path-edit.cancel',
      label: 'Cancel Path Edit',
      category: 'transform',
      hidden: true,
      shortcuts: [{ key: 'Escape' }],
      isAvailable: (ctx) => ctx.pathEditActive,
      run: () => get().cancelPathEdit(),
    },
  );

  // ─── History (Tier 3 Group C Phase 3) ─────────────────────────
  specs.push(
    {
      id: 'history.manual-snapshot',
      label: 'New Snapshot',
      category: 'edit',
      description: 'Pin the current state as a cap-exempt named history anchor',
      // Ctrl+Shift+H — Photoshop's binding for the same feature.
      shortcuts: [{ key: 'h', mod: true, shift: true }],
      run: () => get().historyManualSnapshot(),
    },
    {
      id: 'view.toggle-history-panel',
      label: 'Toggle History Panel',
      category: 'view',
      description: 'Show or hide the History panel — labeled list of every edit with click-to-jump',
      run: () => get().toggleHistoryPanel(),
    },
  );

  // ─── Help / meta ──────────────────────────────────────────────
  specs.push(
    {
      id: 'help.icon-legend',
      label: 'Show Icon Reference',
      category: 'help',
      description: 'Open the icon-meaning cheatsheet',
      run: () => get().openHelp(),
    },
    {
      id: 'palette.open',
      label: 'Open Command Palette',
      category: 'help',
      description: 'Search and run any editor command',
      shortcuts: [{ key: 'k', mod: true }],
      run: () => get().openPalette(),
    },
    {
      id: 'settings.keyboard',
      label: 'Keyboard Shortcuts...',
      category: 'help',
      description: 'Customize keyboard shortcuts',
      // No default shortcut — `Cmd+,` is convention but conflicts
      // with browser-level settings in some platforms, and Phase 2
      // is palette-driven discovery anyway.
      run: () => get().openKeyboardSettings(),
    },
  );

  return specs;
}

/** Exported for tests so future shortcut additions don't accidentally
 *  collide with existing ones (each command has a unique id; each
 *  shortcut tuple is unique within its `isAvailable` band). */
export function shortcutTupleKey(s: CommandShortcut): string {
  return [
    s.code ?? '',
    s.key.toLowerCase(),
    s.mod ? 'M' : '',
    s.ctrl ? 'C' : '',
    s.shift ? 'S' : '',
    s.alt ? 'A' : '',
  ].join(':');
}
